import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardMedia,
  CardContent,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Delete,
  Visibility,
  VisibilityOff,
  Warning,
  CheckCircle,
  Cancel,
  Assignment,
  Check,
  Close,
  ArrowForward,
  ArrowBack,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useGetProductsQuery, useDeleteProductMutation, useHideProductMutation, useReviewProductMutation, useGetProductCommentThreadsQuery } from '../../store/api/adminApi';

const ProductsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // URLパラメータから検索クエリを初期化
  const initialSearchQuery = searchParams.get('search') || '';
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(initialSearchQuery);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [hideDialog, setHideDialog] = useState(false);
  const [productDetailDialog, setProductDetailDialog] = useState(false);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [hideReason, setHideReason] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewAction, setReviewAction] = useState<'approved' | 'rejected'>('approved');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [commentProductId, setCommentProductId] = useState<string>('');
  
  // 検索クエリのデバウンス処理
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPage(0); // 検索時はページをリセット
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: productsData, isLoading, error, refetch } = useGetProductsQuery({
    page: page + 1,
    limit: rowsPerPage,
    status: statusFilter,
    search: debouncedSearchQuery,
  }, {
    // ステータスフィルターが変更されたらキャッシュを無効化
    refetchOnMountOrArgChange: true,
    // パラメータが変更されたら強制的に再フェッチ
    refetchOnFocus: true,
    skip: false,
  });

  const [deleteProduct] = useDeleteProductMutation();
  const [hideProduct] = useHideProductMutation();
  const [reviewProduct] = useReviewProductMutation();
  const { data: commentThreads, isLoading: isLoadingComments } = useGetProductCommentThreadsQuery(
    commentProductId,
    { skip: !commentProductId }
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (event: any) => {
    const newStatus = event.target.value;
    console.log('Status filter changed to:', newStatus);
    setStatusFilter(newStatus);
    setPage(0); // ページを0にリセット
  };

  const handleDeleteProduct = async () => {
    if (!selectedProduct) return;

    try {
      await deleteProduct({
        id: selectedProduct.id,
        reason: deleteReason
      }).unwrap();
      
      setDeleteDialog(false);
      setSelectedProduct(null);
      setDeleteReason('');
      refetch();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleHideProduct = async () => {
    if (!selectedProduct) return;

    try {
      await hideProduct({
        id: selectedProduct.id,
        hidden: true,
        reason: hideReason
      }).unwrap();
      
      setHideDialog(false);
      setSelectedProduct(null);
      setHideReason('');
      refetch();
    } catch (error) {
      console.error('Hide failed:', error);
    }
  };

  const handleReviewProduct = async () => {
    if (!selectedProduct) return;

    try {
      await reviewProduct({
        id: selectedProduct.id,
        approved: reviewAction === 'approved',
        notes: reviewNotes
      }).unwrap();
      
      setReviewDialog(false);
      setSelectedProduct(null);
      setReviewNotes('');
      setReviewAction('approved');
      refetch();
    } catch (error) {
      console.error('Review failed:', error);
    }
  };

  const handleImageNavigation = (direction: 'next' | 'prev') => {
    if (!selectedProduct?.images) return;
    
    const totalImages = selectedProduct.images.length;
    if (direction === 'next') {
      setCurrentImageIndex((prev) => (prev + 1) % totalImages);
    } else {
      setCurrentImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
    }
  };

  const openProductDetail = (product: any) => {
    setSelectedProduct(product);
    setCurrentImageIndex(0);
    setCommentProductId(product.id || '');
    setProductDetailDialog(true);
  };

  const openReviewDialog = (product: any) => {
    setSelectedProduct(product);
    setReviewDialog(true);
  };

  const isSold = (product: any) => {
    return product.buyer && product.buyer.length > 0;
  };

  const getStatusChip = (product: any) => {
    if (isSold(product)) {
      return <Chip label="販売済み" size="small" icon={<CheckCircle />} sx={{ backgroundColor: '#7b1fa2', color: 'white', '& .MuiChip-icon': { color: 'white' } }} />;
    } else if (product.status === 'DELETED') {
      return <Chip label="削除済み" color="error" size="small" icon={<Cancel />} />;
    } else if (product.status === 'SELLER_DELETED') {
      return <Chip label="出品者削除" color="error" size="small" icon={<Cancel />} sx={{ backgroundColor: '#d32f2f', color: 'white' }} />;
    } else if (product.status === 'HIDDEN') {
      return <Chip label="非表示" color="warning" size="small" icon={<VisibilityOff />} />;
    } else if (product.status === 'REJECTED') {
      return <Chip label="拒否済み" color="error" size="small" icon={<Cancel />} />;
    }
    return <Chip label="販売中" color="success" size="small" icon={<CheckCircle />} />;
  };

  const isPendingReview = (product: any) => {
    // 審査中の商品を判定する関数
    return (
      product.reviewStatus === 'pending' ||
      product.status === 'PENDING' ||
      (!product.reviewStatus && product.status !== 'ACTIVE' && product.status !== 'HIDDEN' && product.status !== 'DELETED' && product.status !== 'REJECTED' && product.status !== 'SELLER_DELETED') ||
      (!product.status || product.status === 'REVIEW')
    );
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY'
    }).format(price);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        商品データの読み込みに失敗しました
      </Alert>
    );
  }

  const products = productsData?.items || [];
  const totalProducts = productsData?.totalCount || 0;

  // デバッグ情報を出力（開発環境のみ）
  if (process.env.NODE_ENV === 'development') {
    console.log('Query params:', { page: page + 1, limit: rowsPerPage, status: statusFilter });
    console.log('Products data:', products);
    console.log('Products with pending review:', products.filter(p => isPendingReview(p)));
    console.log('Products statuses:', products.map(p => ({ id: p.id, status: p.status, reviewStatus: p.reviewStatus })));
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        商品管理
      </Typography>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="商品ID、商品名、出品者名で検索"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 300 }}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>ステータス</InputLabel>
          <Select
            value={statusFilter}
            label="ステータス"
            onChange={handleStatusFilterChange}
          >
            <MenuItem value="all">全て</MenuItem>
            <MenuItem value="on_sale">販売中</MenuItem>
            <MenuItem value="sold">販売済み</MenuItem>
            <MenuItem value="hidden">非表示</MenuItem>
            <MenuItem value="deleted">削除済み</MenuItem>
            <MenuItem value="seller_deleted">出品者削除</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>商品</TableCell>
              <TableCell>出品者</TableCell>
              <TableCell>価格</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell>出品日</TableCell>
              <TableCell>アクション</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product: any) => (
              <TableRow key={product.id}>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Card sx={{ width: 60, height: 60, mr: 2 }}>
                      <CardMedia
                        component="img"
                        height="60"
                        image={product.images?.[0] || '/placeholder-image.png'}
                        alt={product.title}
                      />
                    </Card>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {product.title}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        ID: {product.id}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {product.sellerName || 'Unknown'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    ID: {product.sellerId}
                  </Typography>
                  {product.sellerEmail && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      {product.sellerEmail}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{formatPrice(product.price)}</TableCell>
                <TableCell>{getStatusChip(product)}</TableCell>
                <TableCell>
                  {new Date(product.createdAt).toLocaleDateString('ja-JP')}
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Button
                      size="small"
                      variant="outlined"
                      color="info"
                      startIcon={<Visibility />}
                      onClick={() => openProductDetail(product)}
                    >
                      詳細
                    </Button>
                    {isPendingReview(product) && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<Assignment />}
                        onClick={() => openReviewDialog(product)}
                      >
                        審査
                      </Button>
                    )}
                    {product.status === 'ACTIVE' && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        startIcon={<VisibilityOff />}
                        onClick={() => {
                          setSelectedProduct(product);
                          setHideDialog(true);
                        }}
                      >
                        非表示
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => {
                        setSelectedProduct(product);
                        setDeleteDialog(true);
                      }}
                    >
                      削除
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalProducts}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* 商品削除ダイアログ */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>商品削除</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              商品: {selectedProduct?.title}
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              この操作は取り消しできません。
            </Alert>
          </Box>
          <TextField
            autoFocus
            margin="dense"
            label="削除理由"
            type="text"
            fullWidth
            variant="outlined"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            placeholder="削除理由を入力してください"
            multiline
            rows={4}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>
            キャンセル
          </Button>
          <Button 
            onClick={handleDeleteProduct} 
            color="error" 
            variant="contained"
            disabled={!deleteReason.trim()}
          >
            削除実行
          </Button>
        </DialogActions>
      </Dialog>

      {/* 商品非表示ダイアログ */}
      <Dialog open={hideDialog} onClose={() => setHideDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>商品非表示</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              商品: {selectedProduct?.title}
            </Typography>
          </Box>
          <TextField
            autoFocus
            margin="dense"
            label="非表示理由"
            type="text"
            fullWidth
            variant="outlined"
            value={hideReason}
            onChange={(e) => setHideReason(e.target.value)}
            placeholder="非表示にする理由を入力してください"
            multiline
            rows={4}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHideDialog(false)}>
            キャンセル
          </Button>
          <Button 
            onClick={handleHideProduct} 
            color="warning" 
            variant="contained"
            disabled={!hideReason.trim()}
          >
            非表示実行
          </Button>
        </DialogActions>
      </Dialog>

      {/* 商品詳細ダイアログ */}
      <Dialog 
        open={productDetailDialog} 
        onClose={() => setProductDetailDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' }
        }}
      >
        <DialogTitle>商品詳細</DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <Box>
              {/* 商品画像カルーセル */}
              {selectedProduct.images && selectedProduct.images.length > 0 && (
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <Card sx={{ maxWidth: 400, mx: 'auto', mb: 2 }}>
                    <CardMedia
                      component="img"
                      height="300"
                      image={selectedProduct.images[currentImageIndex] || '/placeholder-image.png'}
                      alt={selectedProduct.title}
                    />
                  </Card>
                  {selectedProduct.images.length > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleImageNavigation('prev')}
                        startIcon={<ArrowBack />}
                      >
                        前
                      </Button>
                      <Typography variant="body2">
                        {currentImageIndex + 1} / {selectedProduct.images.length}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleImageNavigation('next')}
                        endIcon={<ArrowForward />}
                      >
                        次
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {/* 商品情報 */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedProduct.title || selectedProduct.name}
                </Typography>
                <Typography variant="h5" color="primary" gutterBottom>
                  {formatPrice(selectedProduct.price)}
                </Typography>
                <Typography variant="body1" paragraph>
                  {selectedProduct.description}
                </Typography>
              </Box>

              {/* 商品詳細情報 */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  商品詳細
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      商品ID
                    </Typography>
                    <Typography variant="body2">
                      {selectedProduct.id}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      カテゴリー
                    </Typography>
                    <Typography variant="body2">
                      {selectedProduct.category || 'なし'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      ステータス
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {getStatusChip(selectedProduct)}
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      出品日
                    </Typography>
                    <Typography variant="body2">
                      {new Date(selectedProduct.createdAt).toLocaleDateString('ja-JP')}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* 出品者情報 */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  出品者情報
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      出品者名
                    </Typography>
                    <Typography variant="body2">
                      {selectedProduct.sellerName || 'Unknown'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      出品者ID
                    </Typography>
                    <Typography variant="body2">
                      {selectedProduct.sellerId}
                    </Typography>
                  </Box>
                  {selectedProduct.sellerEmail && (
                    <>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          メールアドレス
                        </Typography>
                        <Typography variant="body2">
                          {selectedProduct.sellerEmail}
                        </Typography>
                      </Box>
                      <Box></Box>
                    </>
                  )}
                </Box>
              </Box>

              {/* 統計情報 */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  統計情報
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      閲覧数
                    </Typography>
                    <Typography variant="body2">
                      {selectedProduct.viewCount || 0}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      いいね数
                    </Typography>
                    <Typography variant="body2">
                      {selectedProduct.likeCount || 0}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      通報数
                    </Typography>
                    <Typography variant="body2">
                      {selectedProduct.reportCount || 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* 管理者メモ */}
              {selectedProduct.adminNotes && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    管理者メモ
                  </Typography>
                  <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                    {selectedProduct.adminNotes}
                  </Typography>
                </Box>
              )}

              {/* コメント */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  コメント
                </Typography>
                {isLoadingComments ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : commentThreads?.threads && commentThreads.threads.length > 0 ? (
                  commentThreads.threads.map((thread: any, tIdx: number) => (
                    <Paper key={tIdx} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                      <Typography variant="caption" fontWeight="bold" color="primary" mb={0.5} display="block">
                        {thread.userName} のコメント
                      </Typography>
                      <Box sx={{ pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                        {thread.replies.map((reply: any, rIdx: number) => (
                          <Box key={rIdx} sx={{ py: 0.5 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="caption" fontWeight="bold">{reply.userName}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {reply.createdAt ? new Date(reply.createdAt).toLocaleString('ja-JP') : ''}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {reply.comment}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Paper>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    コメントはありません
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setProductDetailDialog(false); setCommentProductId(''); }}>
            閉じる
          </Button>
          {selectedProduct && isPendingReview(selectedProduct) && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Assignment />}
              onClick={() => {
                setProductDetailDialog(false);
                openReviewDialog(selectedProduct);
              }}
            >
              審査する
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 商品審査ダイアログ */}
      <Dialog open={reviewDialog} onClose={() => setReviewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>商品審査</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              商品: {selectedProduct?.title || selectedProduct?.name}
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              出品者: {selectedProduct?.sellerName || 'Unknown'}
            </Typography>
          </Box>
          
          <FormControl sx={{ mb: 2 }} fullWidth>
            <InputLabel>審査結果</InputLabel>
            <Select
              value={reviewAction}
              label="審査結果"
              onChange={(e) => setReviewAction(e.target.value as 'approved' | 'rejected')}
            >
              <MenuItem value="approved">承認（商品を公開）</MenuItem>
              <MenuItem value="rejected">拒否（商品を非公開）</MenuItem>
            </Select>
          </FormControl>

          <TextField
            autoFocus
            margin="dense"
            label={reviewAction === 'approved' ? '審査メモ（任意）' : '拒否理由（必須）'}
            type="text"
            fullWidth
            variant="outlined"
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            placeholder={reviewAction === 'approved' ? '承認に関するメモがあれば入力してください（任意）' : '拒否理由を入力してください（必須）'}
            multiline
            rows={4}
            required={reviewAction === 'rejected'}
            error={reviewAction === 'rejected' && !reviewNotes.trim()}
            helperText={reviewAction === 'rejected' && !reviewNotes.trim() ? '拒否理由の入力は必須です' : ''}
          />
          
          {reviewAction === 'approved' && (
            <Alert severity="success" sx={{ mt: 2 }}>
              この商品は承認され、公開状態になります。メモは任意入力です。
            </Alert>
          )}
          
          {reviewAction === 'rejected' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              この商品は拒否され、非公開状態になります。出品者に通知されます。拒否理由の入力は必須です。
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialog(false)}>
            キャンセル
          </Button>
          <Button 
            onClick={handleReviewProduct} 
            color={reviewAction === 'approved' ? 'success' : 'error'}
            variant="contained"
            disabled={reviewAction === 'rejected' && !reviewNotes.trim()}
            startIcon={reviewAction === 'approved' ? <Check /> : <Close />}
          >
            {reviewAction === 'approved' ? '承認' : '拒否'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductsPage; 